import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock, Filter, ChevronLeft, ChevronRight,
  Plus, Pencil, Trash2, Building2, Users, FolderKanban,
  FileText, Receipt, Presentation, Loader2,
} from 'lucide-react';

import { API } from '../../lib/api';

const ENTITY_ICONS = {
  clients: Building2,
  contacts: Users,
  projects: FolderKanban,
  quotes: FileText,
  invoices: Receipt,
  proposals: Presentation,
};

const ENTITY_COLORS = {
  clients: { bg: '#e0e7ff', text: '#3730a3' },
  contacts: { bg: '#fce7f3', text: '#9d174d' },
  projects: { bg: '#fef3c7', text: '#92400e' },
  quotes: { bg: '#dbeafe', text: '#1d4ed8' },
  invoices: { bg: '#dcfce7', text: '#166534' },
  proposals: { bg: '#ede9fe', text: '#5b21b6' },
};

const ACTION_CONFIG = {
  create: { icon: Plus, bg: '#dcfce7', text: '#166534', label: 'Created' },
  update: { icon: Pencil, bg: '#dbeafe', text: '#1d4ed8', label: 'Updated' },
  delete: { icon: Trash2, bg: '#fee2e2', text: '#dc2626', label: 'Deleted' },
};

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return iso.slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
export default function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const limit = 20;

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, filterEntityType, filterAction]);

  async function loadLogs() {
    setLoading(true);
    try {
      let url = `${API}/audit-logs?limit=${limit}&offset=${offset}`;
      if (filterEntityType) url += `&entity_type=${filterEntityType}`;
      if (filterAction) url += `&action=${filterAction}`;
      const res = await axios.get(url);
      setLogs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Audit logs error:', err);
    } finally {
      setLoading(false);
    }
  }

  function totalPages() {
    return Math.ceil(total / limit);
  }
  function currentPage() {
    return Math.floor(offset / limit) + 1;
  }

  const entityTypes = ['clients', 'contacts', 'projects', 'quotes', 'invoices', 'proposals'];
  const actions = ['create', 'update', 'delete'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5" style={{ color: '#c9a84c' }} />
          <h2 className="text-lg font-bold" style={{ color: '#1a1a2e', fontFamily: "'Space Grotesk', sans-serif" }}>
            Activity Log
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#e5e4df', color: '#6b7280' }}>
            {total} entries
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5" style={{ color: '#6b7280' }} />
          <span className="text-xs font-medium" style={{ color: '#6b7280' }}>Filter:</span>
        </div>

        {/* Entity type filter */}
        <select
          value={filterEntityType}
          onChange={e => { setFilterEntityType(e.target.value); setOffset(0); }}
          className="text-xs border rounded px-2 py-1"
          style={{ borderColor: '#e5e4df', background: '#ffffff', color: '#1a1a2e' }}
        >
          <option value="">All Types</option>
          {entityTypes.map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>

        {/* Action filter */}
        <select
          value={filterAction}
          onChange={e => { setFilterAction(e.target.value); setOffset(0); }}
          className="text-xs border rounded px-2 py-1"
          style={{ borderColor: '#e5e4df', background: '#ffffff', color: '#1a1a2e' }}
        >
          <option value="">All Actions</option>
          {actions.map(a => (
            <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
          ))}
        </select>

        {/* Clear filters */}
        {(filterEntityType || filterAction) && (
          <button
            onClick={() => { setFilterEntityType(''); setFilterAction(''); setOffset(0); }}
            className="text-xs font-medium"
            style={{ color: '#c9a84c' }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Log Timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#c9a84c' }} />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16">
          <Clock className="w-12 h-12 mx-auto mb-3" style={{ color: '#c9a84c', opacity: 0.4 }} />
          <p className="text-sm font-medium" style={{ color: '#1a1a2e' }}>No activity logs yet</p>
          <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
            Actions like creating, updating, or deleting records will appear here
          </p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden" style={{ background: '#ffffff', borderColor: '#e5e4df' }}>
          {/* Table header */}
          <div
            className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-wide border-b"
            style={{ background: '#f8f7f4', borderColor: '#e5e4df', color: '#6b7280' }}
          >
            <div className="col-span-2">Time</div>
            <div className="col-span-2">Action</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-4">Entity</div>
            <div className="col-span-2">User</div>
          </div>

          {/* Rows */}
          {logs.map(log => {
            const actionCfg = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
            const ActionIcon = actionCfg.icon;
            const entityColor = ENTITY_COLORS[log.entity_type] || { bg: '#f3f4f6', text: '#6b7280' };
            const EntityIcon = ENTITY_ICONS[log.entity_type] || Clock;

            return (
              <div
                key={log.id}
                className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center border-b hover:bg-[#f8f7f4] transition-colors"
                style={{ borderColor: '#f3f2ef' }}
              >
                {/* Time */}
                <div className="col-span-2">
                  <span className="text-xs" style={{ color: '#6b7280' }}>{relTime(log.created_at)}</span>
                </div>

                {/* Action */}
                <div className="col-span-2">
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
                    style={{ background: actionCfg.bg, color: actionCfg.text }}
                  >
                    <ActionIcon className="w-3 h-3" />
                    {actionCfg.label}
                  </span>
                </div>

                {/* Type */}
                <div className="col-span-2">
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: entityColor.bg, color: entityColor.text }}
                  >
                    <EntityIcon className="w-3 h-3" />
                    {log.entity_type}
                  </span>
                </div>

                {/* Entity */}
                <div className="col-span-4">
                  <div className="text-xs font-medium truncate" style={{ color: '#1a1a2e' }}>
                    {log.entity_label || log.entity_id?.slice(0, 8) + '...'}
                  </div>
                  {log.details && typeof log.details !== 'object' && (
                    <div className="text-[10px] truncate" style={{ color: '#9ca3af' }}>{String(log.details)}</div>
                  )}
                </div>

                {/* User */}
                <div className="col-span-2">
                  <span className="text-xs" style={{ color: '#6b7280' }}>{log.user}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: '#6b7280' }}>
            Page {currentPage()} of {totalPages()} · {total} total
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium disabled:opacity-30"
              style={{ background: '#f3f2ef', color: '#1a1a2e' }}
            >
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium disabled:opacity-30"
              style={{ background: '#f3f2ef', color: '#1a1a2e' }}
            >
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
